import { Fragment, ReactNode, useEffect, useRef } from 'react';
import { parseSupportAgentMessageContent } from './support-agent-message-content';
import {
  allowlistedKnowledgeUri,
  knowledgePathDisplay,
} from './support-agent-sources';
import { ChatMessage } from './support-agent-types';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Refresh from '@mui/icons-material/Refresh';
import type { SxProps, Theme } from '@mui/material/styles';

interface SupportAgentMessageListProps {
  messages: ChatMessage[];
  isSending: boolean;
  isActive: boolean;
  onRetry: (failedAssistantId: string) => void;
}

export default function SupportAgentMessageList({
  messages,
  isSending,
  isActive,
  onRetry,
}: SupportAgentMessageListProps) {
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isActive) {
      return;
    }

    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isActive]);

  return (
    <Stack
      spacing={1.5}
      sx={{
        flex: 1,
        overflowY: 'auto',
        px: 2,
        py: 2,
      }}
      aria-live="polite"
    >
      {messages.map((message) => {
        const isUser = message.role === 'user';
        const isPending = message.status === 'pending';
        const isError = message.status === 'error';
        const relatedHrefs = (message.sources ?? [])
          .map((source) => allowlistedKnowledgeUri(source.uri))
          .filter((href): href is string => href !== null);

        return (
          <Box
            key={message.id}
            data-testid="support-agent-message"
            data-role={message.role}
            data-status={message.status}
            sx={{
              alignSelf: isUser ? 'flex-end' : 'flex-start',
              maxWidth: '80%',
              px: 1.5,
              py: 1,
              borderRadius: '2px',
              bgcolor: (theme) => {
                if (isUser) {
                  return theme.palette.mode === 'dark'
                    ? theme.palette.colors.primary[400]
                    : theme.palette.colors.primary[50];
                }
                if (isError) {
                  return theme.palette.semantic.surface.error;
                }
                return theme.palette.mode === 'dark'
                  ? theme.palette.colors.primary[700]
                  : theme.palette.semantic.surface.bg1;
              },
              color: (theme) => {
                if (isUser) {
                  return theme.palette.mode === 'dark'
                    ? theme.palette.semantic.button.primary.text
                    : theme.palette.semantic.text.body;
                }
                if (isError) {
                  return theme.palette.semantic.text.error;
                }
                return theme.palette.semantic.text.body;
              },
              border: (theme) => {
                if (isError) {
                  return `1px solid ${theme.palette.semantic.text.error}`;
                }
                return `1px solid ${
                  theme.palette.mode === 'dark'
                    ? theme.palette.colors.white
                    : theme.palette.semantic.outline.light
                }`;
              },
            }}
          >
            {isPending ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress
                  size={14}
                  sx={{
                    color: (theme) =>
                      theme.palette.mode === 'dark'
                        ? theme.palette.colors.primary[300]
                        : theme.palette.semantic.icon.primary,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: (theme) => theme.palette.semantic.text.secondary,
                  }}
                >
                  Thinking…
                </Typography>
              </Stack>
            ) : isError ? (
              <Stack spacing={1}>
                <Typography variant="body2">
                  {message.errorMessage ??
                    'Something went wrong. Please try again.'}
                </Typography>
                <Button
                  size="small"
                  startIcon={<Refresh />}
                  onClick={() => onRetry(message.id)}
                  disabled={isSending}
                  sx={{
                    alignSelf: 'flex-start',
                    color: (theme) => theme.palette.semantic.text.error,
                  }}
                >
                  Retry
                </Button>
              </Stack>
            ) : (
              <Stack spacing={1}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {parseSupportAgentMessageContent(message.content).map(
                    (part, index) =>
                      part.type === 'text' ? (
                        <Fragment key={index}>{part.value}</Fragment>
                      ) : (
                        <KnowledgeArticleLink key={index} href={part.href}>
                          {part.label}
                        </KnowledgeArticleLink>
                      )
                  )}
                </Typography>
                {relatedHrefs.length > 0 ? (
                  <Stack
                    spacing={0.5}
                    component="nav"
                    aria-label="Related Links"
                    sx={{ minWidth: 0 }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: (theme) => theme.palette.semantic.text.secondary,
                      }}
                    >
                      Related Links:
                    </Typography>
                    {relatedHrefs.map((href) => (
                      <KnowledgeArticleLink
                        key={href}
                        href={href}
                        variant="caption"
                      >
                        {knowledgePathDisplay(href)}
                      </KnowledgeArticleLink>
                    ))}
                  </Stack>
                ) : null}
              </Stack>
            )}
          </Box>
        );
      })}
      <div ref={messagesEndRef} />
    </Stack>
  );
}

const knowledgeLinkSx: SxProps<Theme> = (theme) => ({
  wordBreak: 'break-all',
  ...(theme.palette.mode === 'dark' && {
    color: theme.palette.colors.white,
    '&:hover': {
      color: theme.palette.colors.white,
    },
    '&:visited': {
      color: theme.palette.colors.white,
    },
  }),
});

function KnowledgeArticleLink({
  href,
  children,
  variant,
}: {
  href: string;
  children: ReactNode;
  variant?: 'caption';
}) {
  const safeHref = allowlistedKnowledgeUri(href);
  if (!safeHref) {
    return null;
  }

  return (
    <Link
      href={safeHref}
      target="_blank"
      rel="noopener noreferrer"
      variant={variant}
      sx={knowledgeLinkSx}
    >
      {children}
    </Link>
  );
}
